// Admin → School detail. Read-only drill-in: admins, teachers, classes,
// students, subscription. docs/replan-v2.md (admin console, slice 2).

import { Badge } from '@fajrarabi/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useParams } from '@tanstack/react-router';

import { SubscriptionCard } from '~/components/SubscriptionCard';
import { getSchool } from '~/lib/admin';

export const Route = createFileRoute('/_authed/schools/$id')({
  component: SchoolDetailPage,
});

function SchoolDetailPage() {
  const { id } = useParams({ from: '/_authed/schools/$id' });
  const q = useQuery({ queryKey: ['school', id], queryFn: ({ signal }) => getSchool(id, signal) });
  const s = q.data;

  if (q.isLoading) return <Loading />;
  if (!s) return <NotFound />;

  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/schools" className="text-sm text-burgundy-600 hover:underline">
        ← Schools
      </Link>
      <h1 className="mt-2 text-2xl font-semibold text-admin-900">{s.name}</h1>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Badge tone={s.status === 'active' ? 'success' : 'neutral'}>{s.status}</Badge>
        <span className="text-sm text-ink-500">
          {s.country} · {s.slug} · joined {s.createdAt.slice(0, 10)}
        </span>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <Stat label="Teachers" value={s.teachers.length} />
        <Stat label="Classes" value={s.classes.length} />
        <Stat label="Students" value={s.studentCount} />
      </div>

      <SubscriptionCard subscription={s.subscription} className="mt-6" />

      <Panel title="Admins">
        <PeopleList people={s.admins} />
      </Panel>

      <Panel title="Teachers">
        {s.teachers.length === 0 ? (
          <Sub>None</Sub>
        ) : (
          <ul className="space-y-1 text-sm text-ink-700">
            {s.teachers.map((t) => (
              <li key={t.id} className="flex items-center gap-2">
                {t.label}
                <Badge tone={t.status === 'active' ? 'success' : 'neutral'}>{t.status}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Classes">
        {s.classes.length === 0 ? (
          <Sub>None</Sub>
        ) : (
          <ul className="space-y-1 text-sm text-ink-700">
            {s.classes.map((cl) => (
              <li key={cl.id}>
                {cl.name} · {cl.grade} · {cl.studentCount} students
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-burgundy-500/10 bg-white p-5">
      <p className="text-xs uppercase tracking-wider text-ink-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-ink-900">{value}</p>
    </div>
  );
}
function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-4 rounded-2xl border border-burgundy-500/10 bg-white p-5">
      <p className="text-xs uppercase tracking-wider text-ink-500">{title}</p>
      <div className="mt-2">{children}</div>
    </section>
  );
}
function Sub({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-ink-500">{children}</p>;
}
function PeopleList({ people }: { people: Array<{ id: string; label: string }> }) {
  if (people.length === 0) return <Sub>None</Sub>;
  return (
    <ul className="space-y-1 text-sm text-ink-700">
      {people.map((p) => (
        <li key={p.id}>
          <Link to="/users/$id" params={{ id: p.id }} className="text-burgundy-600 hover:underline">
            {p.label}
          </Link>
        </li>
      ))}
    </ul>
  );
}
function Loading() {
  return <div className="mx-auto max-w-3xl text-sm text-ink-500">Loading…</div>;
}
function NotFound() {
  return <div className="mx-auto max-w-3xl text-sm text-heart">School not found.</div>;
}
